import { BookingStage } from "@collins/react-componments/dist/types/booking";
import { ViewBooking, ViewBookingStatusEnum } from "collins-js-sdk";
import dayjs from "dayjs";
import {
  BookableArea,
  bookableAreasClosed,
  Config,
  Type,
  VenueConfig
} from "../../../../types/venues";
import {
  getEndTime,
  getTimeFromString,
  isAreaClosedToday
} from "../../../../utils/booking";
import {
  ClosureInfo,
  FloorplanBooking,
  FloorplanConfig,
  RenderableTable,
  ShapeType,
  TableState,
  Theme
} from "./floorplanTypes";

export const getCustomerDisplayName = (booking: FloorplanBooking): string => {
  if (booking.customerName.toLowerCase().includes("walk in"))
    return "[Walk In]";
  return booking.customerName || "No Name";
};

export const getBookingDurationPercentage = (
  booking: FloorplanBooking
): number => {
  const now = new Date().getTime();
  const start = new Date(booking.startTime).getTime();
  const end = new Date(booking.endTime).getTime();

  if (isNaN(start) || isNaN(end)) return 0;

  const total = end - start;
  const elapsed = now - start;

  return Math.min(Math.max((elapsed / total) * 100, 0), 100);
};

export const formatTime = (isoString: string): string => {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  });
};

export const getStageColour = (
  stages?: BookingStage[],
  stage?: string
): string => {
  return `#${stages?.find((s) => s.id === stage)?.colour ?? "6b7280"}`;
};

export const getStageInitial = (stage?: string): string | undefined => {
  const initial = stage?.trim().charAt(0);
  return initial ? initial.toUpperCase() : undefined;
};

const buildClosureInfo = (closedArea: bookableAreasClosed): ClosureInfo => ({
  timeFrom: closedArea.time_from,
  timeTo: closedArea.time_to,
  isAllDay: !closedArea.time_from || !closedArea.time_to
});

const isClosedNow = (
  closedArea: bookableAreasClosed,
  currentDay: string,
  currentTime: string,
  currentDate: dayjs.Dayjs
): boolean =>
  isAreaClosedToday(closedArea, currentDay, currentTime, currentDate);

const matchesArea = (
  closedArea: bookableAreasClosed,
  areaId: string,
  venueConfig: VenueConfig
): boolean => {
  if (closedArea.type === "areas") {
    return closedArea.areas?.includes(areaId) ?? false;
  }
  if (closedArea.type === "zone" && closedArea.zone) {
    const areaRecord = venueConfig.bookable_areas?.find((a) => a.id === areaId);
    return areaRecord?.zone === closedArea.zone;
  }
  return false;
};

const getAreaClosureInfo = (
  areaId: string,
  area: BookableArea | undefined,
  closures: bookableAreasClosed[],
  currentDate: dayjs.Dayjs,
  venueConfig: VenueConfig
): ClosureInfo | null => {
  if (!area || !closures) return null;
  const currentDay = currentDate.format("dddd").toLowerCase();
  const currentTime = currentDate.format("HH:mm");

  for (const closedArea of closures) {
    if (
      matchesArea(closedArea, areaId, venueConfig) &&
      isClosedNow(closedArea, currentDay, currentTime, currentDate)
    ) {
      return buildClosureInfo(closedArea);
    }
  }
  return null;
};

const doBookingsOverlap = (
  b1: FloorplanBooking,
  b2: FloorplanBooking
): boolean => {
  const s1 = new Date(b1.startTime).getTime();
  const e1 = new Date(
    b1.endTime ?? new Date(s1 + 2 * 60 * 60 * 1000)
  ).getTime();
  const s2 = new Date(b2.startTime).getTime();
  const e2 = new Date(
    b2.endTime ?? new Date(s2 + 2 * 60 * 60 * 1000)
  ).getTime();
  return s1 < e2 && s2 < e1;
};

const getThemeColours = (
  theme: Theme,
  draggedBooking?: FloorplanBooking | null
) => {
  const isDark = theme === "dark";
  const freeDragBg = isDark ? "#064e3b60" : "#f0fdf4";
  const freeDefaultBg = isDark ? "#064e3b20" : "#f0fdf4";
  return {
    isDark,
    freeBg: draggedBooking ? freeDragBg : freeDefaultBg,
    occupiedBg: isDark ? "#1e1b4b30" : "#f5f3ff",
    closedBg: isDark ? "#450a0a20" : "#fff1f2",
    decorBg: isDark ? "#1e293b" : "#f8fafc",
    wallBg: isDark ? "#334155" : "#cbd5e1"
  };
};

const getTableStatus = (
  areaId: string | undefined,
  areaBookings: FloorplanBooking[],
  closureInfo: ClosureInfo | null,
  theme: Theme,
  itemType?: string,
  draggedBooking?: FloorplanBooking | null
): { state: TableState; colour: string; closureInfo?: ClosureInfo } => {
  const colours = getThemeColours(theme, draggedBooking);

  if (!areaId) {
    if (itemType === "wall") return { state: "free", colour: colours.wallBg };
    return { state: "free", colour: colours.decorBg };
  }

  if (closureInfo) {
    return { state: "closed", colour: colours.closedBg, closureInfo };
  }

  if (draggedBooking) {
    const hasOverlapping = areaBookings.some((b) =>
      doBookingsOverlap(b, draggedBooking)
    );
    return {
      state: hasOverlapping ? "occupied" : "free",
      colour: hasOverlapping ? colours.occupiedBg : colours.freeBg
    };
  }

  const isOccupied = areaBookings.length > 0;
  return {
    state: isOccupied ? "occupied" : "free",
    colour: isOccupied ? colours.occupiedBg : colours.freeBg
  };
};

export const getBookingsForFloorplan = (
  bookings: ViewBooking[],
  venueConfig: VenueConfig | null
): FloorplanBooking[] =>
  bookings
    .filter(
      (bookingFilter) => bookingFilter.status !== ViewBookingStatusEnum.Rejected
    )
    .map((b) => mapViewBookingToFloorplanBooking(b, venueConfig));

export const mapViewBookingToFloorplanBooking = (
  booking: ViewBooking,
  venueConfig: VenueConfig | null
): FloorplanBooking => {
  const customerName = booking.walkIn
    ? "[Walk In]"
    : `${booking.firstName ?? ""} ${booking.lastName ?? ""}`.trim() ||
      "No Name";

  const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
  const fallbackIso = "1970-01-01T00:00:00.000Z";

  const endTime = getEndTime(booking, venueConfig ?? undefined);
  const startTimeStr = booking.time
    ? (getTimeFromString(booking.time)?.toISOString() ?? fallbackIso)
    : fallbackIso;
  const endTimeStr = endTime
    ? (getTimeFromString(endTime)?.toISOString() ?? fallbackIso)
    : new Date(new Date(startTimeStr).getTime() + TWO_HOURS_MS).toISOString();

  type Area = { id?: string; name?: string; zone?: string };
  const areas = (booking.assignedAreas as Area[] | undefined) ?? [];

  return {
    id: booking.id ?? "",
    customerName,
    startTime: startTimeStr,
    endTime: endTimeStr,
    guests: booking.numPeople ?? 0,
    assignedAreas: areas.map((a) => ({
      id: String(a.id ?? ""),
      name: String(a.name ?? ""),
      zone: a.zone
    })),
    currentStage: booking.currentStage as FloorplanBooking["currentStage"],
    status: (booking.status as FloorplanBooking["status"]) ?? "Confirmed",
    actualGuests: booking.actualGuests ?? undefined
  };
};

const mapLegacyType = (
  type: Type | undefined,
  areaId: string | undefined
): RenderableTable["decorType"] => {
  if (!type) return undefined;

  switch (type) {
    case "decor":
    case "wall":
    case "label":
    case "outdoor":
      return type;
    case "object":
      return areaId ? undefined : "decor";
    case "table":
      return undefined;
    default:
      return undefined;
  }
};

interface TransformLayoutOptions {
  items: Config[];
  areas: BookableArea[];
  bookings: FloorplanBooking[];
  closures: bookableAreasClosed[];
  showNonTable: boolean;
  theme: Theme;
  currentDate: dayjs.Dayjs;
  venueConfig: VenueConfig;
  draggedBooking?: FloorplanBooking | null;
}

const pushToMap = (
  map: Map<string, FloorplanBooking[]>,
  key: string,
  booking: FloorplanBooking
): void => {
  const list = map.get(key);
  if (list) {
    list.push(booking);
  } else {
    map.set(key, [booking]);
  }
};

const indexBookingsByArea = (
  bookings: FloorplanBooking[]
): {
  active: Map<string, FloorplanBooking[]>;
  display: Map<string, FloorplanBooking[]>;
} => {
  const active = new Map<string, FloorplanBooking[]>();
  const display = new Map<string, FloorplanBooking[]>();

  for (const b of bookings) {
    if (b.currentStage === "left") continue;

    for (const aa of b.assignedAreas) {
      const id = String(aa.id);
      pushToMap(active, id, b);
      if (b.status !== "Rejected") {
        pushToMap(display, id, b);
      }
    }
  }

  return { active, display };
};

interface MapItemContext {
  areasById: Map<string, BookableArea>;
  displayBookings: Map<string, FloorplanBooking[]>;
  activeBookings: Map<string, FloorplanBooking[]>;
  closureInfoByAreaId: Map<string, ClosureInfo | null>;
  theme: Theme;
  draggedBooking?: FloorplanBooking | null;
}

const mapItemToTable = (
  item: Config,
  index: number,
  ctx: MapItemContext
): RenderableTable => {
  const {
    areasById,
    displayBookings,
    activeBookings,
    closureInfoByAreaId,
    theme,
    draggedBooking
  } = ctx;
  const areaId = item.attrs.area_id;
  const areaIdStr = areaId ? String(areaId) : undefined;
  const area = areaIdStr ? areasById.get(areaIdStr) : undefined;

  const tableBookings = areaIdStr ? (displayBookings.get(areaIdStr) ?? []) : [];

  const areaBookingsList = areaIdStr
    ? (activeBookings.get(areaIdStr) ?? [])
    : [];

  const closureInfo = areaIdStr
    ? (closureInfoByAreaId.get(areaIdStr) ?? null)
    : null;

  const resolvedDecorType = mapLegacyType(item.attrs.type, areaIdStr);

  const {
    state,
    colour,
    closureInfo: resolvedClosureInfo
  } = getTableStatus(
    areaIdStr,
    areaBookingsList,
    closureInfo,
    theme,
    resolvedDecorType,
    draggedBooking
  );

  const className = item.className as ShapeType;
  const dimensions = getShapeDimensions(className, item.attrs);

  const isCircle = className === "Circle";
  const circleRadius = isCircle
    ? (item.attrs.radius ?? DEFAULT_CIRCLE_RADIUS)
    : 0;

  return {
    id: areaIdStr ?? `decor-${index}`,
    configId: item.id ?? `config-${index}`,
    name: item.attrs.name ?? area?.name ?? "",
    type: className,
    decorType: resolvedDecorType,
    x: item.attrs.x - circleRadius,
    y: item.attrs.y - circleRadius,
    width: dimensions.width,
    height: dimensions.height,
    rotation: item.attrs.rotation ?? 0,
    area,
    bookings: tableBookings,
    state,
    colour,
    closureInfo: resolvedClosureInfo
  };
};

export const transformLayout = (
  options: TransformLayoutOptions
): RenderableTable[] => {
  const {
    items,
    areas,
    bookings,
    closures,
    showNonTable,
    theme,
    currentDate,
    venueConfig,
    draggedBooking
  } = options;

  const areasById = new Map<string, BookableArea>();
  for (const a of areas) {
    areasById.set(String(a.id), a);
  }

  const { active, display } = indexBookingsByArea(bookings);

  const closureInfoByAreaId = new Map<string, ClosureInfo | null>();
  if (closures.length > 0) {
    for (const item of items) {
      const areaId = item.attrs.area_id;
      if (!areaId) continue;
      const id = String(areaId);
      if (closureInfoByAreaId.has(id)) continue;

      const area = areasById.get(id);
      closureInfoByAreaId.set(
        id,
        getAreaClosureInfo(id, area, closures, currentDate, venueConfig)
      );
    }
  }

  const ctx: MapItemContext = {
    areasById,
    displayBookings: display,
    activeBookings: active,
    closureInfoByAreaId,
    theme,
    draggedBooking
  };

  return items
    .filter((item) => item.attrs.area_id != null || showNonTable)
    .map((item, index) => mapItemToTable(item, index, ctx));
};

// Default shape dimensions in pixels, inherited from the collins-admin floorplan
// renderer to maintain visual parity between the two applications. Values match
// the Konva canvas defaults used in the admin editor's shape palette.
const DEFAULT_CIRCLE_RADIUS = 42; // Standard round table radius (84px diameter)
const DEFAULT_OVAL_WIDTH = 100; // Standard oval table width
const DEFAULT_OVAL_HEIGHT = 80; // Standard oval table height
const DEFAULT_BARSTOOL_SIZE = 40; // Bar stool width and height (square)
const DEFAULT_COMMUNAL_WIDTH = 200; // Communal/long table width
const DEFAULT_COMMUNAL_HEIGHT = 80; // Communal/long table height
const DEFAULT_HIGHTOP_SIZE = 70; // High-top table width and height (square)
const DEFAULT_BANQUETTE_WIDTH = 100; // Banquette/booth table width
const DEFAULT_BANQUETTE_HEIGHT = 80; // Banquette/booth table height
const DEFAULT_RECT_SIZE = 82; // Standard rectangular table width and height

const getShapeDimensions = (
  shape: ShapeType,
  attrs: Config["attrs"]
): { width: number; height: number } => {
  switch (shape) {
    case "Circle":
      return {
        width: (attrs.radius ?? DEFAULT_CIRCLE_RADIUS) * 2,
        height: (attrs.radius ?? DEFAULT_CIRCLE_RADIUS) * 2
      };
    case "Oval":
      return {
        width: attrs.width ?? DEFAULT_OVAL_WIDTH,
        height: attrs.height ?? DEFAULT_OVAL_HEIGHT
      };
    case "BarStool":
      return {
        width: attrs.width ?? DEFAULT_BARSTOOL_SIZE,
        height: attrs.height ?? DEFAULT_BARSTOOL_SIZE
      };
    case "CommunalTable":
      return {
        width: attrs.width ?? DEFAULT_COMMUNAL_WIDTH,
        height: attrs.height ?? DEFAULT_COMMUNAL_HEIGHT
      };
    case "HighTop":
      return {
        width: attrs.width ?? DEFAULT_HIGHTOP_SIZE,
        height: attrs.height ?? DEFAULT_HIGHTOP_SIZE
      };
    case "Banquette":
      return {
        width: attrs.width ?? DEFAULT_BANQUETTE_WIDTH,
        height: attrs.height ?? DEFAULT_BANQUETTE_HEIGHT
      };
    default:
      return {
        width: attrs.width ?? DEFAULT_RECT_SIZE,
        height: attrs.height ?? DEFAULT_RECT_SIZE
      };
  }
};

export const getRotatedBoundingBox = (
  width: number,
  height: number,
  rotationDeg: number
): {
  offsetX: number;
  offsetY: number;
  boundingWidth: number;
  boundingHeight: number;
} => {
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  const corners = [
    { x: 0, y: 0 },
    { x: width * cos, y: width * sin },
    { x: -height * sin, y: height * cos },
    { x: width * cos - height * sin, y: width * sin + height * cos }
  ];

  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);

  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);

  return {
    offsetX: -minX,
    offsetY: -minY,
    boundingWidth: maxX - minX,
    boundingHeight: maxY - minY
  };
};

export const buildFloorplanConfig = (
  mobileConfig: {
    displayNonTableItems: boolean;
    displayTableNames: boolean;
    zoomLevel: number;
  },
  panX?: number,
  panY?: number
): FloorplanConfig => ({
  displayNonTableItems: mobileConfig.displayNonTableItems,
  displayTableNames: mobileConfig.displayTableNames,
  zoomLevel: mobileConfig.zoomLevel,
  panX: panX ?? 0,
  panY: panY ?? 0
});
